import { useEffect, useState, useMemo, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTournamentSocket } from '../../hooks/useTournamentSocket';
import { useAuthStore } from '../../store/auth.store';
import { forcedBets } from '../../lib/forcedBets';
import { getDisplayLevel } from '@pokerroom/types';
import type { TournamentDto, BlindStructureLevelDto, WsTimerUpdate } from '@pokerroom/types';
import { api } from '../../lib/api.ts';
import { getTimerActionError } from './timerActionError';
type ManualCountField = 'entrants' | 'eliminated' | 'rebuys' | 'addons';
async function fetchTournament(id: string): Promise<TournamentDto> {
  return api.get(`/tournament/${id}`);
}
function useCountdown(serverSeconds: number, isRunning: boolean): number {
  const [display, setDisplay] = useState(serverSeconds);
  useEffect(() => {
    setDisplay(serverSeconds);
  }, [serverSeconds]);
  useEffect(() => {
    if (!isRunning) return;
    const interval = setInterval(() => {
      setDisplay((prev) => Math.max(0, prev - 1));
    }, 1000);
    return () => clearInterval(interval);
  }, [isRunning, serverSeconds]);
  return display;
}

export function useTournamentConsole(
  id: string | undefined,
  onFinalizeSuccess?: () => void,
  enabled = true,
  embedded = false,
) {
  const authRevision = useAuthStore((s) => s.authRevision);
  const sessionRevision = useAuthStore((s) => s.sessionRevision);
  const identity = `${id}:${authRevision}:${sessionRevision}`;
  const identityRef = useRef(identity);
  identityRef.current = identity;
  const queryClient = useQueryClient();
  const [finalized, setFinalized] = useState(false);
  const [actionError, setActionError] = useState('');
  const [customSeconds, setCustomSeconds] = useState('');
  const [adjustmentNotice, setAdjustmentNotice] = useState('');
  const adjustmentPending = useRef(false);

  const token = useAuthStore((s) => s.accessToken) ?? undefined;
  const {
    timerUpdate,
    tournamentStatus: tournamentStatusUpdate,
    prizePoolUpdate,
    connected,
    sessionPaused,
    retrySocket,
    sendTimerAction,
    sendTimerActionAsync,
    updateTimerSnapshot,
    socketError,
    clearSocketError,
  } = useTournamentSocket(id, token, enabled);

  const {
    data: tournament,
    refetch,
    error: queryError,
  } = useQuery({
    queryKey: ['tournament', id, authRevision, sessionRevision],
    queryFn: () => fetchTournament(id!),
    enabled:
      !!id &&
      enabled &&
      (!embedded || (connected && !sessionPaused && Boolean(timerUpdate) && !socketError)),
    refetchInterval: 10000,
  });

  useEffect(() => {
    if (tournamentStatusUpdate)
      void queryClient.invalidateQueries({ queryKey: ['tournament', id] });
  }, [tournamentStatusUpdate, queryClient, id]);

  // Blind structure levels from REST (fallback when WS not connected)
  const restLevels = useMemo(() => {
    const bs = (tournament as unknown as { blindStructure?: { levels?: BlindStructureLevelDto[] } })
      ?.blindStructure;
    return bs?.levels ?? [];
  }, [tournament]);

  // WS is real-time, always prefer it; REST as fallback; runningOverride handles pause/resume
  const wsState = timerUpdate?.timerState ?? null;
  const restState =
    (tournament?.timerState as import('@pokerroom/types').TimerState | null) ?? null;
  const state = wsState ?? restState;
  const levelNo = state?.level ?? 1;

  // Use WS currentLevel, or fallback to REST blind structure lookup
  const currentLevel =
    timerUpdate?.currentLevel ??
    restLevels.find((l) => l.levelNo === levelNo) ??
    restLevels[restLevels.length - 1] ??
    null;
  const nextLevel =
    timerUpdate?.nextLevel ?? restLevels.find((l) => l.levelNo === levelNo + 1) ?? null;

  const displayLevel = useMemo(() => getDisplayLevel(restLevels, levelNo), [restLevels, levelNo]);

  // Local override: force isRunning after pause/resume before WS catches up
  const [runningOverride, setRunningOverride] = useState<boolean | null>(null);
  useEffect(() => {
    setFinalized(false);
    setActionError('');
    setCustomSeconds('');
    setAdjustmentNotice('');
    setRunningOverride(null);
    adjustmentPending.current = false;
  }, [identity]);
  const settling = finalized || state?.settling === true || restState?.settling === true;
  const terminal =
    settling || tournament?.status === 'COMPLETED' || tournament?.status === 'CANCELLED';
  const isRunning = !terminal && (runningOverride ?? state?.isRunning ?? false);
  const serverSeconds = state?.remainingSeconds ?? 0;
  const remainingSeconds = useCountdown(
    serverSeconds,
    isRunning && (!embedded || (connected && !sessionPaused && Boolean(timerUpdate))),
  );

  // Refetch tournament data when prize pool updates (sync entries/activePlayers)
  useEffect(() => {
    if (prizePoolUpdate) void refetch();
  }, [prizePoolUpdate, refetch]);

  // Clear override when WS/REST state catches up
  useEffect(() => {
    if (runningOverride !== null && state?.isRunning === runningOverride) {
      setRunningOverride(null);
    }
  }, [state?.isRunning, runningOverride]);
  const isBreak = currentLevel?.isBreak ?? false;

  const sb = currentLevel?.smallBlind ?? 0;
  const bb = currentLevel?.bigBlind ?? 0;
  const anteBba =
    forcedBets(currentLevel?.ante, currentLevel?.bba)
      .map(({ value }) => value.toLocaleString())
      .join(' / ') || '-';
  const nextSb = nextLevel?.smallBlind;
  const nextBb = nextLevel?.bigBlind;
  const nextAnteBba = forcedBets(nextLevel?.ante, nextLevel?.bba)
    .map(({ value }) => value.toLocaleString())
    .join(' / ');

  // 統計一律用後端的 liveStats。手動計數模式沒有 PlayerEntry，
  // 在前端自己過濾 entries 會固定顯示 0。
  const liveStats = tournament?.liveStats;
  const manualMode = liveStats?.manualCountMode ?? false;
  const activePlayers = liveStats?.activePlayers ?? 0;
  const totalEntries = liveStats?.totalEntries ?? 0;
  const rebuyCount = liveStats?.rebuyCount ?? 0;
  const addonCount = liveStats?.addonCount ?? 0;
  // 後端只投射場上人數與總人次，淘汰數由兩者相減得出
  const eliminatedCount = Math.max(0, totalEntries - activePlayers);
  const rawPrizePool = prizePoolUpdate?.prizePool
    ? parseFloat(prizePoolUpdate.prizePool)
    : tournament?.prizePool
      ? parseFloat(tournament.prizePool.toString())
      : 0;
  const prizePool = Number.isFinite(rawPrizePool) ? rawPrizePool : 0;

  const isWarning = remainingSeconds <= 60 && isRunning && !isBreak;
  const tournamentStatus = tournament?.status ?? '';
  const isActive =
    !terminal && (tournamentStatus === 'IN_PROGRESS' || tournamentStatus === 'PAUSED');

  const adjustmentMutation = useMutation({
    retry: false,
    networkMode: 'always',
    mutationFn: (deltaSeconds: number) =>
      api.post<WsTimerUpdate>(`/tournament/${id}/timer/action`, {
        action: 'adjust_time',
        deltaSeconds,
      }),

    onMutate: () => ({ identity, expected: timerUpdate }),
    onSuccess: (update, deltaSeconds, context) => {
      if (context?.identity !== identityRef.current) return;
      updateTimerSnapshot(update, context.expected ?? null);
      setAdjustmentNotice(`已${deltaSeconds > 0 ? '增加' : '減少'} ${Math.abs(deltaSeconds)} 秒`);
    },
    onError: (error, _variables, context) => {
      if (context?.identity === identityRef.current) setActionError(getTimerActionError(error));
    },
    onSettled: (_data, _error, _variables, context) => {
      if (context?.identity === identityRef.current) adjustmentPending.current = false;
    },
  });
  const handleAdjust = (deltaSeconds: number) => {
    if (adjustmentPending.current || !isActive) return;
    setActionError('');
    setAdjustmentNotice('');
    if (!Number.isInteger(deltaSeconds) || deltaSeconds === 0 || Math.abs(deltaSeconds) > 86400) {
      setActionError('請輸入 1 至 86400 的整數秒數');
      return;
    }
    adjustmentPending.current = true;
    adjustmentMutation.mutate(deltaSeconds);
  };
  const validCustomSeconds =
    /^\d+$/.test(customSeconds) && Number(customSeconds) >= 1 && Number(customSeconds) <= 86400;
  const adjustmentDisabled = !isActive || adjustmentMutation.isPending;

  // 手動計數 +/-（只在 manualCountMode 賽事出現）
  const countMutation = useMutation({
    retry: false,
    networkMode: 'always',
    onMutate: () => identity,
    mutationFn: (vars: { field: ManualCountField; delta: -1 | 1 }) =>
      api.patch(`/tournament/${id}/manual-counts`, vars),
    onSuccess: (_data, _variables, context) => {
      if (context !== identityRef.current) return;
      setActionError('');
      void queryClient.invalidateQueries({ queryKey: ['tournament', id] });
    },
    onError: (err: Error, _variables, context) => {
      if (context !== identityRef.current) return;
      setActionError(getTimerActionError(err));
      console.warn('adjustManualCount failed:', err.message);
    },
  });

  // 確認賽事結束（進入結算模式）
  const finalizeMutation = useMutation({
    retry: false,
    networkMode: 'always',
    onMutate: () => identity,
    mutationFn: () => api.post(`/tournament/${id}/finalize-play`),
    onSuccess: (_data, _variables, context) => {
      if (context !== identityRef.current) return;
      setFinalized(true);
      setActionError('');
      void queryClient.invalidateQueries({ queryKey: ['tournament', id] });
      onFinalizeSuccess?.();
    },
    onError: (err: Error, _variables, context) => {
      if (context !== identityRef.current) return;
      setActionError(getTimerActionError(err));
      console.warn('finalizePlay failed:', err.message);
    },
  });

  // 暫停賽事（暫停 + 狀態切換）
  const pauseMutation = useMutation({
    retry: false,
    networkMode: 'always',
    onMutate: () => identity,
    mutationFn: () => api.post(`/tournament/${id}/pause`),
    onSuccess: (_data, _variables, context) => {
      if (context !== identityRef.current) return;
      setActionError('');
      setRunningOverride(false);
      void refetch();
    },
    onError: (err: Error, _variables, context) => {
      if (context !== identityRef.current) return;
      setActionError(getTimerActionError(err));
      console.warn('pauseTournament failed:', err.message);
    },
  });

  // 恢復賽事
  const resumeMutation = useMutation({
    retry: false,
    networkMode: 'always',
    onMutate: () => identity,
    mutationFn: () => api.post(`/tournament/${id}/resume`),
    onSuccess: (_data, _variables, context) => {
      if (context !== identityRef.current) return;
      setActionError('');
      setRunningOverride(true);
      void refetch();
    },
    onError: (err: Error, _variables, context) => {
      if (context !== identityRef.current) return;
      setActionError(getTimerActionError(err));
      console.warn('resumeTournament failed:', err.message);
    },
  });

  // 開賽（DRAFT/REGISTERED → IN_PROGRESS）。
  // WS 的 start 只會讓時鐘跑起來，賽事狀態仍是 DRAFT，接著按暫停就會被
  // lifecycle guard 拒絕。手動計數模式的計數器在這一頁，操作員本來就會
  // 在開賽前先按人數，所以這一頁必須能真正開賽。
  const startMutation = useMutation({
    retry: false,
    networkMode: 'always',
    onMutate: () => identity,
    mutationFn: () => api.post(`/tournament/${id}/start`),
    onSuccess: (_data, _variables, context) => {
      if (context !== identityRef.current) return;
      setActionError('');
      setRunningOverride(true);
      void refetch();
    },
    onError: (err: Error, _variables, context) => {
      if (context !== identityRef.current) return;
      setActionError(getTimerActionError(err));
      console.warn('startTournament failed:', err.message);
    },
  });

  const handleStart = () => {
    if (terminal || pauseMutation.isPending || resumeMutation.isPending || startMutation.isPending)
      return;
    if (tournamentStatus === 'PAUSED') {
      resumeMutation.mutate();
    } else if (tournamentStatus === 'DRAFT' || tournamentStatus === 'REGISTERED') {
      startMutation.mutate();
    } else {
      sendTimerAction({ action: 'start' });
    }
  };

  const handlePause = () => {
    if (pauseMutation.isPending || resumeMutation.isPending) return;
    pauseMutation.mutate();
  };

  const handleStop = () => {
    if (!confirm('確認賽事結束？將進入結算模式，可在牌桌管理中進行籌碼點算。')) return;
    finalizeMutation.mutate();
  };

  return {
    dataError: queryError ? getTimerActionError(queryError) : '',
    settling,
    terminal,
    tournament,
    actionError,
    setActionError,
    customSeconds,
    setCustomSeconds,
    adjustmentNotice,
    timerUpdate,
    connected,
    sessionPaused,
    retrySocket,
    sendTimerAction,
    sendTimerActionAsync,
    socketError,
    clearSocketError,
    restLevels,
    currentLevel,
    nextLevel,
    displayLevel,
    isRunning,
    remainingSeconds,
    isBreak,
    sb,
    bb,
    anteBba,
    nextSb,
    nextBb,
    nextAnteBba,
    manualMode,
    activePlayers,
    totalEntries,
    rebuyCount,
    addonCount,
    eliminatedCount,
    prizePool,
    isWarning,
    isActive,
    adjustmentMutation,
    handleAdjust,
    validCustomSeconds,
    adjustmentDisabled,
    countMutation,
    finalizeMutation,
    pauseMutation,
    resumeMutation,
    startMutation,
    handleStart,
    handlePause,
    handleStop,
  };
}
